import React, { useEffect, useRef, useState } from 'react';
import styles from './NexsetDetail.module.scss';
import { Subheader } from 'new/elements/Subheader';
import { Nexset } from 'new/entities/Nexset';
import { SourceName } from 'new/elements/SourceName';
import { Typography } from '@mui/material';
import { ChipBadge } from 'new/elements/ChipBadge';
import { Button } from 'new/elements/Button';
import { IconButton } from 'new/elements/IconButton';
import { Stack } from 'new/elements/Stack';
import { ScreenComponent } from 'new/routes/RouterManager';
import { ROUTE_REDESIGN } from 'js/constants/pathnames';
import { ROUTE_DISCOVER } from 'new/constants/pathnames';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { fetchDatasetDoc, fetchResourceNotificationsPaginated, loadDataSet } from 'js/actions';
import { useNexset } from 'new/hooks/adapters/useNexset';
import { ReactComponent as SendIcon } from 'new/components/icons/send.svg';
import { ReactComponent as TransformIcon } from 'new/components/icons/transform.svg';
import { useIsLoading } from 'new/hooks/adapters/useIsLoading';
import { GET_DATA_SET_REQUEST, GET_DATA_SETS_REQUEST } from 'js/constants/actionTypes';
import { CenteredLoader } from 'new/elements/CenteredLoader';
import { Tabs } from 'new/elements/Tabs';
import { NexsetActions } from 'new/elements/NexsetActions';
import { NexsetOverview } from 'new/elements/NexsetOverview';
import { ModalEditDoc } from 'new/elements/ModalEditDoc';
import { Activity } from 'new/elements/Activity';
import { RESOURCE_TYPE_DATA_SET } from 'js/constants/resourceTypes';
import { Notifications } from 'new/elements/Notifications';
import { DATASET } from 'js/constants/notificationsTypes';
import { NexsetSchemaViewer } from 'new/elements/NexsetSchemaViewer';
import { NexsetStats } from 'new/elements/NexsetStats';
import { NexsetShare } from 'new/components/discover/NexsetDetail/NexsetShare';

interface NexsetDetailProps {}

const Status = (props: { status: string }) => {
  const { status } = props;
  const info = {
    ACTIVE: {
      color: 'success',
      title: 'Active',
    },
    INACTIVE: {
      color: 'danger',
      title: 'Inactive',
    },
    PAUSED: {
      color: 'disabled',
      title: 'Paused',
    },
  };
  const data = info[status] || info.ACTIVE;
  return <ChipBadge label={data.title} color={data.color} />;
};

const TitleComponent = (props: Partial<Nexset>) => {
  return (
    <Stack direction="row" alignItems="center">
      <SourceName type={props.isDetected ? 'source' : 'nexset'} icon={props.sourceIcon} />
      <Stack>
        <Stack alignItems="center" direction="row" gap="4px">
          <Typography variant="subtitle1">{props.name}</Typography>
          <Status status={props.status} />
        </Stack>
        <Typography variant="body2">{props.description}</Typography>
      </Stack>
    </Stack>
  );
};

const Actions = (props: Nexset) => {
  return (
    <div className={styles.actions}>
      <Button variant="contained">Preview Data Flow</Button>
      <IconButton>
        <TransformIcon />
      </IconButton>
      <IconButton>
        <SendIcon />
      </IconButton>
      <NexsetActions {...props} />
    </div>
  );
};

const tabs = [
  {
    label: 'Overview',
    value: 'overview',
    Component: NexsetOverview,
  },
  {
    label: 'Schema',
    value: 'schema',
    Component: NexsetSchemaViewer,
  },
  {
    label: 'Stats',
    value: 'stats',
    Component: NexsetStats,
  },
  {
    label: 'Notifications',
    value: 'notifications',
    Component: Notifications,
  },
  {
    label: 'Activity',
    value: 'activity',
    Component: Activity,
  },
  {
    label: 'Error data',
    value: 'error',
  },
  {
    label: 'Share',
    value: 'share',
    Component: NexsetShare,
  },
];

export const NexsetDetail: ScreenComponent<NexsetDetailProps> = (props: NexsetDetailProps) => {
  const { id } = useParams<{ id: string }>();
  const tabsRef = useRef();
  const [tab, setTab] = useState(tabs[0]);
  const dispatch = useDispatch();
  const loading = useIsLoading(GET_DATA_SETS_REQUEST, GET_DATA_SET_REQUEST);
  const aclMode = useSelector((state: any) => state.app.listing.aclQueryMode);
  const nexset = useNexset(id);

  useEffect(() => {
    if (!nexset) {
      dispatch(loadDataSet(id, { aclQueryMode: aclMode, ignoreErrors: false }));
    }
    dispatch(
      fetchResourceNotificationsPaginated(id, DATASET, {
        page: 1,
        per_page: 100,
      }),
    );
    dispatch(fetchDatasetDoc(id));
  }, []);

  const onTabChange = (e, value) => {
    setTab(tabs.find((t) => t.value === value));
  };

  if (loading) {
    return <CenteredLoader height="500px" />;
  }

  const Component = tab?.Component || React.Fragment;

  return (
    <Stack className={styles.container}>
      <div className={styles.header}>
        <Subheader
          TitleComponent={<TitleComponent {...nexset} />}
          RightComponent={<Actions {...nexset} />}
        />
      </div>
      <div ref={tabsRef} className={styles.tabs}>
        <Tabs value={tab.value} tabs={tabs} onChange={onTabChange} />
      </div>
      <Component
        tabsRef={tabsRef}
        resourceType={RESOURCE_TYPE_DATA_SET}
        resourceId={nexset.id + ''}
        notificationType={DATASET}
        data={nexset}
        nexset={nexset}
      />
    </Stack>
  );
};

NexsetDetail.screenConfig = {
  screenName: 'NexsetDetail',
  path: `${ROUTE_REDESIGN}${ROUTE_DISCOVER}/nexsets/:id`,
  domain: 'discover',
  modals: [ModalEditDoc],
};
